/* eslint-disable max-lines */
// Package Imports
import { Button, Card, Flex } from 'antd';
// prettier-ignore
import { BaseRecord, HttpError, UseSelectProps, useDelete, useOne, useTable, useTranslate, useUpdate, } from '@refinedev/core';
import { useForm, useSelect, useSimpleList } from '@refinedev/antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
// Project Imports
import resourceName from 'core/common/resourceName';
import RulePlansForm from 'pages/rulePlans/RulePlansForm';
import { UseSelectParameterType } from 'pages/program/interface';
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import constants from 'core/constants/Constants';
import openNotificationWithIcon from 'core/utils/notification';
import replacePipe from 'core/utils/replacePipe';
import Loading from 'components/loading/loading';
import { useNavigate } from 'react-router-dom';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import getOnRuleChange from 'pages/rulePlans/getOnRuleChange';

type Props = {
  type: string;
  refetch?: any;
};
const RulePlansCreate = ({ type, refetch }: Props) => {
  const t = useTranslate();
  const { setRulePlanDataObj, rulePlanDataObj } = useRulesContext();
  const { userDetails, compensationType } = useTokenContext();
  const programId = userDetails?.iprogram;
  const [isUnifiedDeleted, setIsUnifiedDeleted] = useState(false);
  const [sendApproval, setSendApproval] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const navigate = useNavigate();
  const [pageType, setPageType] = useState<string>(
    type === 'Edit' ? 'Show' : type
  );
  const [statusModal, setStatusModal] = useState(false);
  const { data: ipData } = useOne({
    dataProviderName: 'organizationDataProvider',
    resource: apiEndpoint.incentiveProgram,
    id: programId,
    queryOptions: { enabled: !!programId },
  });
  const isRuleApproval = ipData?.data?.results?.rule_approval;
  const { onFinish, formProps, saveButtonProps, formLoading, form } = useForm({
    resource: `${type === 'Show' ? apiEndpoint.rulesPlan : apiEndpoint.rulesPlanDraft}`,
    redirect: false,
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: (data) => {
      setRulePlanDataObj(data?.data?.results);
      const message = data?.data?.message || t('notifications.createSuccess');
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        message
      );
      navigate(
        `/${resourceName.rulePlans}/edit/${data?.data?.results?.edit_unique_id}`
      );
      setPageType('Show');
    },
    onMutationError(error) {
      const message = error?.message || t('notifications.createError');
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        message
      );
    },
  });
  const [isUnifiedChange, setIsUnifiedChange] = useState(false);
  function getOptionList(resourcename, dpName, label, value) {
    const useSelectParameter: UseSelectProps<
      UseSelectParameterType,
      HttpError,
      UseSelectParameterType
    > = {
      resource: resourcename,
      dataProviderName: dpName,
      optionLabel: label,
      optionValue: value,
      meta: {
        type: 'select',
      },
    };
    const { selectProps } = useSelect(useSelectParameter);
    return (selectProps as any)?.options;
  }
  const resourceUrl = `${resourceName.unifiedSourceList}/?page=1&page_size=1000`;
  const unifiedList = getOptionList(
    resourceUrl,
    'unifiedDataProvider',
    'unified_source_name',
    'unique_id'
  );
  if (unifiedList) {
    if (type === 'Create' && unifiedList?.length === 1) {
      const value = unifiedList?.[0]?.value;
      form.setFieldValue('unified_source_master_id', value);
    }
  }
  const { tableQueryResult } = useTable({
    resource: `${type === 'Show' && rulePlanDataObj?.view_unique_id ? `${apiEndpoint.rulesPlan}/${rulePlanDataObj?.view_unique_id}` : `${apiEndpoint.rulesPlanDraft}/${rulePlanDataObj?.edit_unique_id}`}`,
    queryOptions: {
      enabled: !!rulePlanDataObj?.unique_id,
    },
    pagination: {
      mode: 'off',
    },
    errorNotification: (data: BaseRecord) => {
      const message =
        replacePipe(data?.message) || t('notifications.errorMessage');
      return {
        message: 'Error Message',
        description: message,
        type: 'error',
      };
    },
  });
  const rulesData = (tableQueryResult?.data?.data as BaseRecord)?.results;
  const { mutate } = useUpdate();
  const { mutate: deleteMutate } = useDelete();
  useEffect(() => {
    getOnRuleChange(rulesData, unifiedList, form, setIsUnifiedDeleted);
  }, [rulesData, unifiedList]);
  const onFinishHandler = async (value: any) => {
    setSaveLoading(true);
    if (type === 'Show' || pageType === 'Show') {
      setSaveLoading(false);
      return;
    }
    const getDaterange = value?.rule_plans_date;
    let startDate = dayjs();
    let endDate = dayjs();
    if (getDaterange) {
      startDate = getDaterange?.[0];
      endDate = getDaterange?.[1];
    }
    let rulePlanCompensationType = value.rule_plan_compensation_type;
    if (compensationType === 'cash') {
      rulePlanCompensationType = 'cash';
    }

    if (compensationType === 'points') {
      rulePlanCompensationType = 'points';
    }

    if (rulesData) {
      if (
        !statusModal &&
        rulesData?.rule_plan_is_active !== value?.rule_plan_is_active
      ) {
        setStatusModal(true);
        setSaveLoading(false);
        return;
      }
      mutate(
        {
          resource: `${type === 'Show' ? apiEndpoint.rulesPlan : apiEndpoint.rulesPlanDraft}`,
          values: {
            ...value,
            rule_plan_compensation_type: rulePlanCompensationType,
            rule_plan_start_date: dayjs(startDate).format('YYYY-MM-DD'),
            rule_plan_end_date: dayjs(endDate).format('YYYY-MM-DD'),
            unified_source_master_name: unifiedList.filter(
              (item) => item.value === value.unified_source_master_id
            )[0].label,
          },
          id: rulePlanDataObj?.unique_id,
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: (data) => {
            const message =
              data?.data?.message || t('notifications.createSuccess');
            setStatusModal(false);
            setRulePlanDataObj(data?.data?.results);
            openNotificationWithIcon(
              constants.success,
              t('notifications.mutationSuccess'),
              message
            );
            setPageType('Show');
            refetch();
            if (isUnifiedChange) {
              deleteMutate(
                {
                  resource: `${apiEndpoint.deleteRulesAfterUniChange}`,
                  dataProviderName: 'programDataProvider',
                  id: rulePlanDataObj?.unique_id,
                  successNotification: false,
                  errorNotification: false,
                },
                {
                  onSuccess: (delData) => {
                    const delMessage =
                      delData?.data?.message ||
                      t('notifications.createSuccess');
                    openNotificationWithIcon(
                      constants.success,
                      t('notifications.mutationSuccess'),
                      delMessage
                    );
                    setIsUnifiedChange(false);
                  },
                  onError(error) {
                    const delMessage =
                      error?.message || t('notifications.somethingError');
                    openNotificationWithIcon(
                      constants.error,
                      t('notifications.mutationError'),
                      delMessage
                    );
                  },
                }
              );
            }
            setSaveLoading(false);
          },
          onError(error) {
            const message = error?.message || t('notifications.somethingError');
            openNotificationWithIcon(
              constants.error,
              t('notifications.mutationError'),
              message
            );
            setSaveLoading(false);
          },
        }
      );
    } else {
      await onFinish({
        ...value,
        rule_plan_compensation_type: rulePlanCompensationType,
        rule_plan_start_date: dayjs(startDate).format('YYYY-MM-DD'),
        rule_plan_end_date: dayjs(endDate).format('YYYY-MM-DD'),
        unified_source_master_name: unifiedList.filter(
          (item) => item.value === value.unified_source_master_id
        )[0].label,
      });
      setSaveLoading(false);
    }
  };
  const handleOnChange = () => {
    setPageType('Edit');
  };

  const { listProps: response, queryResult } = useSimpleList({
    resource: `${apiEndpoint.sendRulePlanForApproval}/${rulesData?.edit_unique_id}`,
    pagination: { mode: 'off' },
    queryOptions: { enabled: false, cacheTime: 0 },
  });
  const approvalStatus = (response?.dataSource as any)?.status || '';

  useEffect(() => {
    setSendApproval(false);
    if (approvalStatus === 'success') {
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        (response?.dataSource as any)?.message ||
          t('rulePlans.messages.sentForApproval')
      );
      refetch();
      navigate(`/${resourceName.rulePlans}`);
    }
  }, [approvalStatus]);

  const disabled = false;
  if (tableQueryResult?.isFetching) return <Loading />;
  return (
    <>
      {type !== 'Show' && isRuleApproval && rulesData?.status === 'pending' ? (
        <Flex justify="end">
          <Button
            className="mb-lg"
            onClick={async () => {
              if (!rulesData?.edit_unique_id) return;
              await queryResult.refetch();
            }}
            type="primary"
          >
            {t('buttons.sendForApproval')}
          </Button>
        </Flex>
      ) : null}
      <div
        className={`relative ${pageType === 'Show' ? 'rule-details-view' : ''}`}
      >
        <Card>
          <RulePlansForm
            formData={{
              form,
              onFinish,
              formLoading,
              formProps,
              saveButtonProps,
            }}
            eventHandler={{ onFinishHandler, refetch }}
            stateFunctionHandler={{
              setIsUnifiedChange,
              setIsUnifiedDeleted,
              setStatusModal,
              setSendApproval,
            }}
            stateData={{
              unifiedList,
              rulesData,
              isUnifiedDeleted,
              statusModal,
              isRuleApproval,
              sendApproval,
              saveLoading,
            }}
            staticData={{ type: pageType }}
            configData={{ disabled }}
          />
          {type !== 'Create' && type !== 'Show' && pageType !== 'Edit' ? (
            <Button
              icon={<EditOutlined />}
              className="absolute right-4 top-2 w-10 h-10 flex justify-center items-center border-primary-500 text-primary-500"
              onClick={() => handleOnChange()}
            />
          ) : null}
        </Card>
      </div>
    </>
  );
};
export default RulePlansCreate;
